import * as React from 'react';
import { observer } from 'mobx-react';
import { ManagerBudget } from 'nomst/Stores/Budget/managerBudget';
import { ManagersCountryBudget } from 'nomst/Stores/Budget/countryBudget';
import './groupedBudgetView.scss';
import { editBudgetViewStore } from 'utils/MobxDataManager/singletons';
import { CycleBudget } from 'nomst/Stores/Configuration/cycleBudget';
import { FormGroup, Input } from '@payscale/design';
import { Configuration } from 'nomst/Stores/Configuration/configuration';

export interface IGroupBudgetViewProps {
    groupedSummaryBudget: ManagersCountryBudget;
    budget: CycleBudget
    useAutoSync: boolean;
    configuration: Configuration;
    renderManagerBudgets: (managers: ManagerBudget[], turnOffTargetBudgetPercentageUpdateFlag: () => void, turnOffTargetBudgetValueUpdateFlag: () => void) => React.ReactNode
}

const GroupBudgetView = (props: IGroupBudgetViewProps): React.ReactElement => {
    const { groupedSummaryBudget, budget, useAutoSync, configuration, renderManagerBudgets } = props;

    const renderGroupedBudgetHeaderRow = (): React.ReactNode => {
        return (
            <tr className="grouped-budget-view__header">
                <td>{groupedSummaryBudget.groupByFlag}</td>
                <td hidden={!editBudgetViewStore.shouldShowTotalCurrentPay(budget)}>{groupedSummaryBudget.totalCurrentPayText}</td>
                <td hidden={editBudgetViewStore.shouldShowOriginalBudget(budget) === false}>{groupedSummaryBudget.originalBudgetText}</td>
                <td hidden={!editBudgetViewStore.useProration}>{groupedSummaryBudget.adjustedBudgetBeforeProrationText}</td>
                <td hidden={!editBudgetViewStore.useProration}>{groupedSummaryBudget.proratedBudgetText}</td>
                <td hidden={!editBudgetViewStore.shouldShowAdjustedTargetPercentage(budget)}>
                    {renderAdjustedBudgetPercentageForGroupedManagers()}
                </td>
                <td>{renderAdjustedBudget()}</td>
                <td>{groupedSummaryBudget.totalSpentText}</td>
                <td>{groupedSummaryBudget.budgetBalanceText}</td>
            </tr>
        );
    };

    const renderAdjustedBudgetPercentageForGroupedManagers = (): React.ReactNode => {
        if (useAutoSync) {
            return (
                <span data-mf-replace-inner="***">{groupedSummaryBudget.adjustedBudgetPercentageText}</span>
            );
        }

        if (budget.isStockBudgetInUnitOnly) {
            return (
                <Input
                    value={groupedSummaryBudget.adjustedBudgetText}
                    id="increaseByValueGroupedManager"
                    disabled={editBudgetViewStore.shouldDisableInput(budget)}
                    onChange={handleChangeValue}
                    onBlur={handleOnBlurValue}
                    className="increase-by-value-grouped-manager"
                />
            );
        }

        return (
            <FormGroup>
                <Input
                    value={groupedSummaryBudget.adjustedBudgetPercentageText}
                    id="increaseByPercentageGroupedManager"
                    disabled={editBudgetViewStore.shouldDisableInput(budget)}
                    onChange={handleChangePercentage}
                    onBlur={handleOnBlurPercentage}
                    className="increase-by-percentage-grouped-manager"
                />
            </FormGroup>
        );
    };

    const renderAdjustedBudget = (): React.ReactNode => {
        if (useAutoSync) {
            return (
                <span data-mf-replace-inner="***">{groupedSummaryBudget.adjustedBudgetText}</span>
            );
        }

        return (
            <FormGroup>
                <Input
                    value={groupedSummaryBudget.adjustedBudgetText}
                    disabled={isManagerBudgetInputDisabled()}
                    onChange={handleChangeValue}
                    onBlur={handleOnBlurValue}
                    className="increase-by-value-grouped-manager"
                />
            </FormGroup>
        );
    };

    const isManagerBudgetInputDisabled = (): boolean => {
        return groupedSummaryBudget.totalCurrentPay === 0
        || configuration.isClosed
        || configuration.isClosing;
    };

    const handleChangePercentage = (_: undefined, value: string): void => {
        groupedSummaryBudget.setTargetBudgetPercentage(value);
    };

    const handleOnBlurPercentage = (): void => {
        groupedSummaryBudget.onBlurTargetBudgetPercentage();
    };

    const handleChangeValue = (_: undefined, value: string): void => {
        groupedSummaryBudget.setTargetBudgetValue(value);
    };

    const handleOnBlurValue = (): void => {
        groupedSummaryBudget.onBlurTargetBudgetValue();
    };

   
    return (
        <tbody className="grouped-budget-view">
            {renderGroupedBudgetHeaderRow()}
            {renderManagerBudgets(groupedSummaryBudget.managers, groupedSummaryBudget.turnOffTargetBudgetPercentageUpdateFlag, groupedSummaryBudget.turnOffTargetBudgetValueUpdateFlag)}
        </tbody>
    );
};

export default observer(GroupBudgetView);
